import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const ACCENT_THEMES = {
  red: {
    gradient: "linear-gradient(135deg, #ef4444 0%, #2563eb 100%)",
    softBg: "rgba(239,68,68,0.08)",
    softBorder: "rgba(239,68,68,0.15)",
    strip: "#ef4444",
    text: "#2563eb",
    iconBg: "rgba(239,68,68,0.1)",
    iconColor: "#2563eb",
  },
  indigo: {
    gradient: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)", // Brand Red override
    softBg: "rgba(37,99,235,0.07)",
    softBorder: "rgba(37,99,235,0.15)",
    strip: "#2563eb",
    text: "#2563eb",
    iconBg: "rgba(37,99,235,0.1)",
    iconColor: "#2563eb",
  },
  blue: {
    gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
    softBg: "rgba(59,130,246,0.07)",
    softBorder: "rgba(59,130,246,0.15)",
    strip: "#3b82f6",
    text: "#2563eb",
    iconBg: "rgba(59,130,246,0.1)",
    iconColor: "#2563eb",
  },
  emerald: {
    gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    softBg: "rgba(16,185,129,0.07)",
    softBorder: "rgba(16,185,129,0.15)",
    strip: "#10b981",
    text: "#059669",
    iconBg: "rgba(16,185,129,0.1)",
    iconColor: "#059669",
  },
  amber: {
    gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    softBg: "rgba(245,158,11,0.07)",
    softBorder: "rgba(245,158,11,0.15)",
    strip: "#f59e0b",
    text: "#d97706",
    iconBg: "rgba(245,158,11,0.1)",
    iconColor: "#d97706",
  },
  slate: {
    gradient: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
    softBg: "rgba(100,116,139,0.07)",
    softBorder: "rgba(100,116,139,0.15)",
    strip: "#64748b",
    text: "#475569",
    iconBg: "rgba(100,116,139,0.1)",
    iconColor: "#475569",
  },
  violet: {
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
    softBg: "rgba(139,92,246,0.07)",
    softBorder: "rgba(139,92,246,0.15)",
    strip: "#8b5cf6",
    text: "#7c3aed",
    iconBg: "rgba(139,92,246,0.1)",
    iconColor: "#7c3aed",
  },
};

function getTrendMeta(trend) {
  if (!trend) return null;
  const isDown = trend.toLowerCase().includes("down") || trend.toLowerCase().includes("limit") || trend.startsWith("-");
  const isNeutral = trend.toLowerCase().includes("no change") || trend === "0%";
  return {
    color: isDown ? "#ef4444" : isNeutral ? "#94a3b8" : "#10b981",
    bg: isDown ? "rgba(239,68,68,0.08)" : isNeutral ? "rgba(148,163,184,0.08)" : "rgba(16,185,129,0.08)",
    border: isDown ? "rgba(239,68,68,0.2)" : isNeutral ? "rgba(148,163,184,0.2)" : "rgba(16,185,129,0.2)",
    Icon: isDown ? TrendingDown : isNeutral ? Minus : TrendingUp,
  };
}

export function KpiCard({ label, value, icon: Icon, trend, accent = "indigo", testId, compact = false, onClick, hero = false }) {
  const theme = ACCENT_THEMES[accent] || ACCENT_THEMES.indigo;
  const trendMeta = getTrendMeta(trend);
  const isLongLabel = typeof label === "string" && label.length > 15;
  const valueText = value == null ? "" : String(value);
  const isLongValue = valueText.length > 7;
  const CardRoot = onClick ? motion.button : motion.div;
  const actionProps = onClick ? {
    type: "button",
    onClick,
    "aria-label": `View ${label} hoardings`,
  } : {};
  const interactiveClass = onClick
    ? "cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
    : "";

  if (compact) {
    return (
      <CardRoot
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ y: -2, transition: { duration: 0.18 } }}
        data-testid={testId}
        className={`relative overflow-hidden p-3.5 flex flex-col gap-2 hx-card ${interactiveClass}`}
        {...actionProps}
      >
        {/* Colored top strip */}
        <div className="absolute inset-x-0 top-0 h-0.5 rounded-t-[10px]" style={{ background: theme.strip }} />

        <div className="flex items-center justify-between gap-2 relative">
          <div className="flex-1 min-w-0">
            <p className="font-display text-[9px] font-bold tracking-[0.08em] uppercase text-slate-500 leading-tight mb-1.5 whitespace-nowrap select-none">
              {label}
            </p>
            <p className="text-base font-bold font-mono text-slate-900 leading-none whitespace-nowrap tracking-tight">
              {value}
            </p>
          </div>
          {Icon && (
            <div
              className="flex-shrink-0 w-6.5 h-6.5 rounded-md flex items-center justify-center"
              style={{ background: theme.iconBg }}
            >
              <Icon size={12} strokeWidth={2.5} style={{ color: theme.iconColor }} />
            </div>
          )}
        </div>
        {trendMeta && trend && (
          <div
            className="flex items-center gap-1 text-[9px] font-bold leading-none px-1.5 py-0.5 rounded-md w-fit"
            style={{ color: trendMeta.color, background: trendMeta.bg }}
          >
            <trendMeta.Icon size={9} strokeWidth={2.5} className="flex-shrink-0" />
            <span>{trend}</span>
          </div>
        )}
      </CardRoot>
    );
  }

  return (
    <CardRoot
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -2, transition: { duration: 0.18 } }}
      data-testid={testId}
      className={`relative overflow-hidden p-5 flex flex-col gap-2.5 hx-card ${interactiveClass}`}
      {...actionProps}
    >
      {hero && (
        <>
          {/* Colored top accent — reserved for hero stats */}
          <div className="absolute inset-x-0 top-0 h-[3px] rounded-t-[10px]" style={{ background: theme.gradient }} />
          {/* Subtle corner glow */}
          <div
            className="absolute top-0 right-0 w-24 h-24 rounded-bl-full opacity-[0.05] pointer-events-none"
            style={{ background: theme.gradient }}
          />
        </>
      )}

      <div className="flex items-start justify-between gap-3 relative">
        <div className="flex-1 min-w-0">
          <p className="font-display text-[10px] font-bold tracking-[0.1em] uppercase text-slate-500 mb-2 leading-tight whitespace-nowrap select-none">
            {label}
          </p>
          <p className={`font-mono ${isLongValue ? "text-2xl" : "text-3xl"} font-bold text-slate-900 leading-none whitespace-nowrap tracking-tight`}>
            {value}
          </p>
        </div>
        {Icon && (
          hero ? (
            <div
              className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center shadow-sm mt-0.5"
              style={{ background: theme.gradient }}
            >
              <Icon size={16} strokeWidth={2} className="text-white" />
            </div>
          ) : (
            <div
              className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5"
              style={{ background: theme.softBg, color: theme.text }}
            >
              <Icon size={16} strokeWidth={2} />
            </div>
          )
        )}
      </div>

      {trendMeta && trend && (
        <div
          className="flex items-center gap-x-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-md w-fit max-w-full border"
          style={{ color: trendMeta.color, background: trendMeta.bg, borderColor: trendMeta.border }}
        >
          <trendMeta.Icon size={10} strokeWidth={2.5} />
          <span>{trend}</span>
          <span className="text-slate-400 font-medium">vs last month</span>
        </div>
      )}
    </CardRoot>
  );
}
